import React, { useEffect, useRef, useState } from 'react';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import IconButton from '@mui/material/IconButton';
import MuiTooltip from '@mui/material/Tooltip';
import { Bar, BarChart, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, ComposedChart, Line } from 'recharts';
import useAxiosProtect from '../../../../hooks/useAxiosProtect';
import RefreshIcon from '@mui/icons-material/Refresh';

const TimeseriesGraph = props => {
    var init = useRef(true);

    const [timeframe, setTimeframe] = useState('1h');
    const [flowieTimeframe, setFlowieTimeframe] = useState('1w');
    const [seriesType, setSeriesType] = useState('usage');
    const [data, setData] = useState([]);

    const axiosProtect = useAxiosProtect();

    const getTankLevelData = siteID => {
        axiosProtect.get(`/asbr/blueroof/timeseries-data?id=${siteID}&timeframe=${timeframe}&fields[]=${'tank_ultrasonic'}`).then(response => {
            setData(response.data.data);

            init.current = false;
        }).catch(error => {
            init.current = false;
        });
    }

    const getFlowieTimeframeValues = () => {
        const startDate = new Date();
        const endDate = new Date();

        if(flowieTimeframe === '1w'){
            startDate.setDate(endDate.getDate() - 7);
        }else if(flowieTimeframe === '1m'){
            startDate.setDate(endDate.getDate() - 31);
        }

        const format = (date) => date.toISOString().split('T')[0];

        return {startDate: format(startDate), endDate: format(endDate)};
    }

    const getFlowieData = (siteID) => {
        let {startDate, endDate} = getFlowieTimeframeValues();

        axiosProtect.get(`/asbr/blueroof/flowie-timeseries-data?siteID=${siteID}&startDate=${startDate}&endDate=${endDate}`).then(response => {
            let keys = Object.keys(response.data.readings);
            if(keys.length === 0){
                setData([]);
                return;
            }

            let dataLength = response.data.readings[keys[0]].values.length;
            let dataSeries = [];
            for(let i = 0;i<dataLength;++i){
                let readingObject = {
                    time: response.data.readings[keys[0]].values[i].date
                };

                for(let j = 0;j<keys.length;++j){
                    readingObject[response.data.readings[keys[j]].name] = response.data.readings[keys[j]].values[i].total_litres;
                }

                dataSeries.push(readingObject);
            }

            setData(dataSeries)
            console.log(dataSeries)

            init.current = false;
        }).catch(error => {
            init.current = false;
        });
    }

    useEffect(() => {
        if(!init.current) return;

        let siteID = window.location.href.split('/').slice(-1);
        if(parseInt(siteID) !== 14) return;

        getFlowieData(siteID)

    }, []);

    useEffect(() => {
        if(init.current) return;

        let siteID = window.location.href.split('/').slice(-1);
        if(parseInt(siteID) !== 14) return;

        getTankLevelData(siteID);
    }, [timeframe]);

    useEffect(() => {
        if(init.current) return;

        let siteID = window.location.href.split('/').slice(-1);
        if(parseInt(siteID) !== 14) return;

        getFlowieData(siteID);
    }, [flowieTimeframe]);

    useEffect(() => {
        let siteID = window.location.href.split('/').slice(-1);
        if(parseInt(siteID) !== 14) return;

        if(seriesType === 'usage') getFlowieData(siteID);
        else if(seriesType === 'tank') getTankLevelData(siteID);
    }, [seriesType]);

    const formatYAxisTick = tick => {
        if (Math.abs(tick) >= 1e6) return (tick / 1e6).toFixed(1) + 'M'; 
        else if (Math.abs(tick) >= 1e4) return (tick / 1e3).toFixed(0) + 'k';
        else return tick.toLocaleString();
    }

    const formatReadings = (reading, label) => {
        let isFlowieData = seriesType === 'usage';
        return [`${(Math.round(reading * 100) / 100).toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} ${isFlowieData ? 'L' : 'cm'}`, isFlowieData ? label : 'Tank level'];
    }

    const formatXAxisTick = (tick) => {
        if(seriesType === 'usage') return tick?.toString().substring(0,10);
        
        const date = new Date(tick);
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const formattedHours = hours % 12 || 12;
        const month = date.toLocaleString('default', { month: 'numeric' });
        const day = date.getDate();
    
        return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${ampm} ${month}/${day}`;
    };

    return (
        <div style={{height:'100%', width:'100%'}}>
             <div style={{height:'85%'}}>

                <div style={{width:'100%', height:'100%', margin:'1% auto'}}>
                    <div style={{display:'flex', flexDirection:'row', padding: '0 1%'}}>
                        <div style={{textAlign:'left'}}>
                            <ToggleButtonGroup
                                value={seriesType}
                                exclusive
                                onChange={(e,v) => {
                                    if(!v) return;
                                    setSeriesType(v);
                                }}
                                aria-label="text alignment"
                                size='small'
                            >
                                <ToggleButton value='usage' aria-label="left aligned">
                                    <p className="m-0">Usage</p>
                                </ToggleButton>
                                <ToggleButton value='tank' aria-label="left aligned">
                                    <p className="m-0">Tank level</p>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </div>

                        <div>
                            <MuiTooltip title="Refresh graph" placement='right'>
                                <IconButton onClick={() => {
                                    let siteID = window.location.href.split('/').slice(-1);
                                    if(parseInt(siteID) !== 14) return;
                            
                                    if(seriesType === 'usage') getFlowieData(siteID);
                                    else if(seriesType === 'tank') getTankLevelData(siteID);
                                }}>
                                    <RefreshIcon/>
                                </IconButton>
                            </MuiTooltip>
                        </div>

                        <div style={{flex:'1'}}></div>

                        <div style={{textAlign:'right'}}>
                            {
                                seriesType === 'usage' ?
                                <ToggleButtonGroup
                                    value={flowieTimeframe}
                                    exclusive
                                    onChange={(e,v) => {
                                        if(!v) return;
                                        setFlowieTimeframe(v)
                                    }}
                                    aria-label="text alignment"
                                    size='small'
                                >
                                    <ToggleButton value='1w' aria-label="left aligned">
                                        <p className="m-0">1 week</p>
                                    </ToggleButton>
                                    <ToggleButton value='1m' aria-label="left aligned">
                                        <p className="m-0">1 month</p>
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                :
                                <ToggleButtonGroup
                                    value={timeframe}
                                    exclusive
                                    onChange={(e,v) => {
                                        if(!v) return;
                                        setTimeframe(v)
                                    }}
                                    aria-label="text alignment"
                                    size='small'
                                >
                                    <ToggleButton value='1h' aria-label="left aligned">
                                        <p className="m-0">1 hour</p>
                                    </ToggleButton>
                                    <ToggleButton value='1d' aria-label="left aligned">
                                        <p className="m-0">1 day</p>
                                    </ToggleButton>
                                    <ToggleButton value='1w' aria-label="left aligned">
                                        <p className="m-0">1 week</p>
                                    </ToggleButton>
                                    <ToggleButton value='1m' aria-label="left aligned">
                                        <p className="m-0">1 month</p>
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            }
                        </div>
                    </div>

                     <ResponsiveContainer height='100%' width='100%'>
                        <ComposedChart data={data}>
                            <XAxis dataKey="time" tickLine={false} axisLine={false} tick={{ fontSize: '12px', fill: 'rgb(78,79,83)'}} 
                                tickFormatter={formatXAxisTick} interval={Math.ceil(data.length / 7)} textAnchor='start'/>
        
                            <YAxis yAxisId="left" tickLine={false} axisLine={false} tickFormatter={formatYAxisTick} tick={{ fontSize: '12px', fill: 'rgb(78,79,83)'}}
                                label={{value: seriesType === 'usage' ? 'Water usage (L)' : 'Tank level (cm)', position: 'outside', offset: 0, angle: -90, dx: -25, style: { fontSize: '12px', fill: 'rgb(78,79,83)'}}}
                                stroke={`rgb(8,114,182)`} strokeWidth={1.5} dot={false}/>
                            
                            <Tooltip 
                                contentStyle={{
                                    backgroundColor: '#F5F5F5',
                                    color: '#333',
                                    border:'1px solid lightgrey',
                                    fontSize: '0.75em',
                                }}
                                labelFormatter={formatXAxisTick}
                                wrapperStyle={{ outline: "none" }}
                                formatter={formatReadings}
                                cursor={{strokeWidth: seriesType === 'usage' ? flowieTimeframe === '1w' ? 7 * 5 * 2 : 32 / 2 : 1 }}
                            />
        
                            <CartesianGrid stroke="#f5f5f5" />
                            {
                                seriesType === 'usage' ? 
                                <>
                                {
                                    Object.keys(data[0] || {}).filter(key => key !== 'time').map((flowie, i) => (
                                        <Bar key={flowie} dataKey={flowie} fill={i % 2 === 0 ? 'rgb(8,114,182)' : 'rgb(0,177,168)'} yAxisId="left" />
                                    ))
                                }
                                </> 
                                :
                                <Line yAxisId="left" dataKey='tank_ultrasonic' fill={props.primary} dot={false}/>
                            }
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}

export default TimeseriesGraph;